import React, { FC, useCallback, useEffect, useId, useState } from 'react';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';

import KEYS from '@rover/constants/src/platform/keys.constants';
import { DropdownSmall } from '@rover/icons';
import { Box, Flex } from '@rover/kibble/core';
import useLocationContext from '@rover/react-lib/src/context/useLocationContext';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';

import NavBarItem from '../../NavBarItem';
import MenuItem from '../MenuItem';
import { getShouldShowItem, MenuItemType, MenuItemVisibility } from '../utilities';

import Avatar from './Avatar';

const DESKTOP_MENU_WIDTH = '158px';
const DESKTOP_MENU_ITEM_HEIGHT = '34px';

const getMenuItems = (currentUserSlug?: string): MenuItemType[] => [
  {
    path: '/account/',
    displayName: t`Dashboard`,
    visibility: [MenuItemVisibility.SITTER, MenuItemVisibility.OWNER],
  },
  {
    path: '/provider-profile/',
    displayName: t`Profile`,
    visibility: [MenuItemVisibility.SITTER],
  },
  {
    path: '/account/profile/',
    displayName: t`Profile`,
    visibility: [MenuItemVisibility.OWNER],
  },
  {
    path: '/account/inbox/',
    displayName: t`Inbox`,
    visibility: [MenuItemVisibility.SITTER, MenuItemVisibility.OWNER],
  },
  {
    path: '/account/calendar/',
    displayName: t`Calendar`,
    visibility: [MenuItemVisibility.SITTER],
  },
  {
    path: '/account/profile/settings/',
    displayName: t`Settings`,
    visibility: [MenuItemVisibility.SITTER, MenuItemVisibility.OWNER],
  },
  ...(currentUserSlug
    ? [
        {
          path: `/members/${currentUserSlug}/dogs/`,
          displayName: t`Your Pets`,
          visibility: [MenuItemVisibility.SITTER, MenuItemVisibility.OWNER],
        },
      ]
    : []),
  {
    path: '/provider-profile/photos/',
    displayName: t`Photos`,
    visibility: [MenuItemVisibility.SITTER],
  },
];

const DesktopMenu: FC = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const { isActiveServiceProvider, logout, user } = useCurrentUserShim();
  const { pathname } = useLocationContext();
  const menuContainerId = useId();
  const userFirstName = user?.firstName ?? '';

  const menuItems: MenuItemType[] = getMenuItems(user?.slug);

  const totalItems =
    menuItems.reduce(
      (acc, curr) =>
        getShouldShowItem(curr.visibility, true, isActiveServiceProvider ?? false) ? acc + 1 : acc,
      0
    ) + 1;

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false);
  }, [setIsDropdownOpen]);

  const toggleDropdown = (): void => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  useEffect(() => {
    if (!isDropdownOpen) {
      // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
      document.removeEventListener('click', closeDropdown);
    }
  }, [isDropdownOpen, closeDropdown]);

  // Handles document event listener
  useEffect(() => {
    const eventListenerArgs = ['click', closeDropdown, false] as const;
    if (isDropdownOpen) {
      // we need the setTimeout to ensure the listener is added after the click is handled
      setTimeout(() => {
        document.addEventListener(...eventListenerArgs);
      }, 0);
    } else {
      document.removeEventListener(...eventListenerArgs);
    }
    // return cleanup function
    return () => document.removeEventListener(...eventListenerArgs);
  }, [isDropdownOpen, closeDropdown]);

  const handleMenuItemFocus = (index: number): void => {
    if (process.env.JS_ENV_CLIENT) {
      if (index > 0) {
        setCurrentIndex(index);
        const currentItem = document.querySelector<HTMLElement>(
          `#navbar-dropdown-menu-option-${index} a, #navbar-dropdown-menu-option-${index} button`
        );
        if (currentItem) {
          currentItem.focus();
        }
      } else if (index === 0) {
        const menuToggle = document.querySelector<HTMLElement>(
          `#navbar-dropdown-menu-toggle > button`
        );
        if (menuToggle) {
          menuToggle.focus();
        }
      }
    }
  };

  const handleOnButtonKeyDown = (event): void => {
    // When the menu is closed but focused, a press on Enter or the up/down arrows should open it
    switch (event.key) {
      case KEYS.DOWN:
      case KEYS.UP:
        event.preventDefault();
        setIsDropdownOpen(true);
        if (!isDropdownOpen || event.key !== KEYS.UP) {
          handleMenuItemFocus(1);
        }
        break;

      default:
        break;
    }
  };

  const handleKeyDown = (event): void => {
    const prevIndex = currentIndex - 1;
    const nextIndex = currentIndex + 1;

    // Selection should not follow focus
    // Source: https://www.w3.org/TR/wai-aria-practices/#kbd_selection_follows_focus
    switch (event.key) {
      case KEYS.UP:
        event.preventDefault();
        if (prevIndex >= 0) {
          handleMenuItemFocus(prevIndex);
        }

        break;

      case KEYS.DOWN:
        event.preventDefault();
        if (nextIndex < totalItems + 1) {
          handleMenuItemFocus(nextIndex);
        }

        break;
      case KEYS.TAB:
        if (event.shiftKey) {
          setCurrentIndex(currentIndex - 1);
        } else {
          setCurrentIndex(currentIndex + 1);
        }
        break;

      default:
        break;
    }
  };

  let index = 0;
  const items = menuItems.map((menuItem) => {
    const shouldShowItem = getShouldShowItem(
      menuItem.visibility,
      true,
      isActiveServiceProvider ?? false
    );
    index = shouldShowItem ? index + 1 : index;
    return (
      shouldShowItem && (
        <MenuItem
          key={menuItem.displayName}
          href={menuItem.path}
          onKeyDown={handleKeyDown}
          id={`navbar-dropdown-menu-option-${index}`}
          height={DESKTOP_MENU_ITEM_HEIGHT}
        >
          <Trans id={menuItem.displayName} />
        </MenuItem>
      )
    );
  });

  return (
    <Flex
      position="relative"
      alignSelf="stretch"
      alignItems="center"
      display={['none', 'none', 'flex']}
      minWidth="min-content"
    >
      <NavBarItem
        id="navbar-dropdown-menu-toggle"
        aria-controls={menuContainerId}
        aria-expanded={isDropdownOpen}
        aria-label={t`Logged in as ${userFirstName}`}
        onClick={toggleDropdown}
        onKeyDown={handleOnButtonKeyDown}
        icon={DropdownSmall}
        iconAlignment="right"
      >
        <Flex alignItems="center" gap="2x">
          <Avatar />
          {userFirstName}
        </Flex>
      </NavBarItem>
      <Flex
        width={DESKTOP_MENU_WIDTH}
        background="navigation"
        borderBottomLeftRadius="secondary"
        borderBottomRightRadius="secondary"
        overflow="hidden"
        display={isDropdownOpen ? 'initial' : 'none'}
        flexDirection="column"
        position="absolute"
        right="0"
        top="100%"
      >
        <Box
          aria-activedescendant={`navbar-dropdown-menu-option-${currentIndex}`}
          id={menuContainerId.toString()}
          as="ul"
          m="0x"
          p="0x"
        >
          {items}

          {/* divider */}
          <Box height="1px" background="info" />

          <MenuItem
            key="Logout"
            onClick={() =>
              logout &&
              logout({
                nextLocation: pathname,
                ref: 'header',
              })
            }
            onKeyDown={handleKeyDown}
            id={`navbar-dropdown-menu-option-${index + 1}`}
            tabIndex={0}
            height={DESKTOP_MENU_ITEM_HEIGHT}
          >
            <Trans id="Log Out" />
          </MenuItem>
        </Box>
      </Flex>
    </Flex>
  );
};

export default DesktopMenu;
